import { useEffect, useCallback, useState } from 'react';
import { useMapStateStore, useMapDataStore } from '../stores';
import { buildYourTourApiUrl, fetchYourTourApi } from '../utils/ApiHelpers';
import { getDataVersion } from '../utils/dataVersions';
import useHeartbeat from './useHeartbeat';

const MAP_CACHE_PREFIX = 'yt-map-data:';

// Cache TTL can be long since we use version-based invalidation.
// This is just the maximum age before we discard the cache entirely.
// On each page load, we check the version and refetch if stale.
const MAP_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Custom hook to fetch and manage map data.
 * Fetches all map-related data in a single optimized API call.
 * Uses version-based cache invalidation to detect stale data.
 *
 * Cache strategy:
 * 1. Show cached data immediately (instant UX)
 * 2. Call lightweight /data-versions endpoint to check if data changed
 * 3. If version differs, fetch fresh data in background
 * 4. If version matches, cache is still valid - no refetch needed
 *
 * Live updates via heartbeat polling (5 seconds on `yt`, 60 seconds on every
 * other platform — see useHeartbeat):
 * - Map structure changes (rare): full map refetch
 * - Map occupancy changes (frequent): lightweight occupancy-only refetch
 *   via /map-occupancy endpoint (~5-10KB vs ~100KB+ for full map)
 *
 * Polling only runs when the platform actually displays occupancy. Where it
 * does not, the app would be polling to detect changes to data it never
 * renders. The trade-off is that mapStructure and communityConfig changes are
 * no longer picked up mid-session on those platforms; both are admin-driven,
 * change rarely, and land on the next page load.
 *
 * @param {string} urlSlug - The community URL slug
 * @param {string} platform - The current platform (yt, kiosk, siteSee, etc.)
 * @param {boolean} showOccupancy - platformShowOccupancy; gates heartbeat polling
 */
const useMapData = ( urlSlug, platform, showOccupancy = false ) => {
  // Map state store
  const setInitialLevelSvgId = useMapStateStore( ( state ) => state.setInitialLevelSvgId );

  // Map data store
  const setMapData = useMapDataStore( ( state ) => state.setMapData );
  const setOccupancyData = useMapDataStore( ( state ) => state.setOccupancyData );

  // Track whether initial data has loaded (heartbeat should only start after).
  const [ heartbeatEnabled, setHeartbeatEnabled ] = useState( false );

  const cacheKey = `${MAP_CACHE_PREFIX}${urlSlug}`;

  const readCache = useCallback( () => {
    try {
      const raw = window?.localStorage?.getItem( cacheKey );
      if ( !raw ) {
        return null;
      }
      const parsed = JSON.parse( raw );
      if ( !parsed?.timestamp || !parsed?.data ) {
        return null;
      }
      return parsed;
    } catch ( error ) {
      console.warn( 'Unable to read map cache:', error );
      return null;
    }
  }, [ cacheKey ] );

  const writeCache = useCallback( ( data ) => {
    try {
      window?.localStorage?.setItem(
        cacheKey,
        JSON.stringify( { timestamp: Date.now(), data } ),
      );
    } catch ( error ) {
      console.warn( 'Unable to write map cache:', error );
    }
  }, [ cacheKey ] );

  // Fetch full map data (structural changes or initial load).
  const fetchAllMapData = useCallback( async () => {
    try {
      const version = await getDataVersion( urlSlug, 'map', platform );
      const url = buildYourTourApiUrl(
        `/your_tour_api/map/${urlSlug}`,
        version ? { v: version } : {},
        platform,
      );
      const data = await fetchYourTourApi( url, platform );

      if ( data && !data.error ) {
        setMapData( data );
        if ( data.initialLevelSvgId ) {
          setInitialLevelSvgId( data.initialLevelSvgId );
        }
        writeCache( data );
      }
    } catch ( error ) {
      console.error( 'Error fetching map data:', error );
    }
  }, [ urlSlug, platform, setMapData, setInitialLevelSvgId, writeCache ] );

  // Fetch only occupancy data (status changes — lightweight ~5-10KB).
  const fetchOccupancyData = useCallback( async () => {
    try {
      const version = await getDataVersion( urlSlug, 'map', platform );
      const url = buildYourTourApiUrl(
        `/your_tour_api/map-occupancy/${urlSlug}`,
        version ? { v: version } : {},
        platform,
      );
      const data = await fetchYourTourApi( url, platform );

      if ( data && !data.error ) {
        setOccupancyData( data );
      }
    } catch ( error ) {
      console.error( 'Error fetching occupancy data:', error );
    }
  }, [ urlSlug, platform, setOccupancyData ] );

  // Heartbeat callbacks.
  const handleMapStructureChange = useCallback( () => {
    fetchAllMapData();
  }, [ fetchAllMapData ] );

  const handleMapOccupancyChange = useCallback( () => {
    fetchOccupancyData();
  }, [ fetchOccupancyData ] );

  // Poll the heartbeat endpoint once initial data is loaded, and only where
  // the platform displays the occupancy data the heartbeat exists to refresh.
  useHeartbeat( urlSlug, platform, {
    onMapStructureChange: handleMapStructureChange,
    onMapOccupancyChange: handleMapOccupancyChange,
    enabled: heartbeatEnabled && !!showOccupancy,
  } );

  // Fetch all map-related data in a single optimized API call.
  useEffect( () => {
    if ( !urlSlug || !platform ) {
      return undefined;
    }

    let isCancelled = false;

    const checkVersion = async ( cachedVersion ) => {
      try {
        const url = buildYourTourApiUrl( `/your_tour_api/data-versions/${urlSlug}`, {}, platform );
        const versionData = await fetchYourTourApi( url, platform );
        if ( versionData?.versions?.map && versionData.versions.map !== cachedVersion ) {
          return true; // Version changed, cache is stale
        }
        return false; // Version matches, cache is fresh
      } catch ( error ) {
        console.warn( 'Unable to check map version:', error );
        return true; // On error, assume stale and refetch
      }
    };

    const initializeMapData = async () => {
      const cached = readCache();
      const cacheAge = cached ? Date.now() - cached.timestamp : null;

      // If we have cached data that's not too old, use it immediately
      if ( cached && cacheAge < MAP_CACHE_MAX_AGE_MS ) {
        setMapData( cached.data );
        if ( cached.data?.initialLevelSvgId ) {
          setInitialLevelSvgId( cached.data.initialLevelSvgId );
        }

        // Check if the cached version is still fresh via lightweight endpoint
        const cachedVersion = cached.data?.dataVersion;
        if ( cachedVersion ) {
          const isStale = await checkVersion( cachedVersion );
          if ( isStale && !isCancelled ) {
            // Version changed, fetch fresh data
            await fetchAllMapData();
          }
        } else {
          // No version in cache (old cache format), refetch
          await fetchAllMapData();
        }
      } else {
        // No cache or cache too old, fetch fresh data
        await fetchAllMapData();
      }

      // Enable heartbeat polling now that initial data is loaded.
      if ( !isCancelled ) {
        setHeartbeatEnabled( true );
      }
    };

    initializeMapData();

    return () => {
      isCancelled = true;
    };
  }, [ urlSlug, platform, setMapData, setInitialLevelSvgId, readCache, fetchAllMapData ] );
};

export default useMapData;
